import type { CompanyInfoBase, EntitlementsInfo, ImpersonationStatus, OrganizationBase, OrganizationMember, OrganizationUserRole, SubscriptionTier, UserlandBaseUser } from '@/client/types.gen'
import { createContext, useContext } from 'react'

export interface AuthState {
  user: UserlandBaseUser | undefined
  isLoading: boolean
  isAuthenticated: boolean

  // Derived conveniences
  isSupplycoStaff: boolean
  organizationId: string | undefined
  organizationName: string | undefined
  isOrgAdmin: boolean
  currentUserRole: OrganizationUserRole | undefined
  subscriptionTier: SubscriptionTier | undefined
  isTrialOrg: boolean
  isDistributorOrg: boolean
  companyInfo: CompanyInfoBase | undefined
  currentAccountCount: number
  maxAccounts: number
  isTrialLimitReached: boolean
  remainingAccountSlots: number
  currentUserCount: number
  maxSeats: number | undefined
  error: string | undefined

  // Entitlements
  entitlements: EntitlementsInfo | undefined
  canImportAccounts: boolean
  canDeleteAccounts: boolean
  canEditOrgSettings: boolean
  canShareWithPartners: boolean
  canManageDistributors: boolean
  canEditPersonas: boolean
  canEditOutreach: boolean
  canExportAccounts: boolean
  canUseMarketResearch: boolean
  canUseIntegrations: boolean
  canUseOrderInbox: boolean
  canUseScoutLeads: boolean
  canViewCrm: boolean
  canUseAgentRuns: boolean

  // Organization info
  organizationInfo: OrganizationBase | undefined
  organizationMembers: OrganizationMember[] | undefined

  // Impersonation state
  impersonation: ImpersonationStatus | undefined

  refetch: () => Promise<void>
  login: (username: string, password: string) => Promise<UserlandBaseUser>
  magicLinkCallback: (token: string) => Promise<UserlandBaseUser>
  /**
   * Sync the auth context with a cookie-based session that some other backend
   * flow already established (demoday signup, magic-link onboarding token
   * exchange). Sets the localStorage flag AND the React `hasSession` state,
   * then refetches the user. Required after non-credential login so logout's
   * downstream redirect doesn't run against stale router context.
   */
  markSessionEstablished: () => Promise<void>
  logout: () => Promise<void>
}

export const AuthContext = createContext<AuthState | undefined>(
  undefined,
)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
